/**
 * Cart Attributes Cleanup
 * Keeps delivery details and greeting (and all other cart attributes) even after
 * the customer completes checkout. Cleanup is disabled so this information is
 * never deleted—neither when going to checkout nor on the thank you page.
 */
(function () {
  'use strict';

  /**
   * Clear all cart attributes
   * @returns {Promise<void>}
   */
  async function clearCartAttributes() {
    try {
      // Get current cart to preserve non-custom attributes if needed
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) return;

      const cart = await cartResponse.json();
      const existingAttrs = cart.attributes || {};

      // List of attributes to clear (gift card and delivery scheduling)
      const attributesToClear = [
        'receiver_name',
        'receiver_phone',
        'receiver_address',
        'card_message',
        'media_link',
        'keep_my_identity_secret',
        'delivery_date',
        'delivery_date_saved_at',
      ];

      // Build new attributes object without the ones we want to clear
      /** @type {Record<string, any>} */
      const updatedAttrs = {};
      for (const key in existingAttrs) {
        if (!attributesToClear.includes(key)) {
          updatedAttrs[key] = existingAttrs[key];
        }
      }

      // Update cart with cleared attributes
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({ attributes: updatedAttrs })
      });
    } catch (error) {
      console.error('Error clearing cart attributes:', error);
    }
  }

  /**
   * Check if cart is empty and clear attributes if needed
   */
  async function checkAndClearIfEmpty() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return;

      const cart = await response.json();
      
      // If cart is empty, clear attributes
      if (!cart.items || cart.items.length === 0) {
        await clearCartAttributes();
      }
    } catch (error) {
      console.error('Error checking cart status:', error);
    }
  }

  /**
   * Handle cart update events
   * @param {Event} event
   */
  function handleCartUpdate(event) {
    // Check if this is a CartUpdateEvent with itemCount
    // The event detail structure: event.detail.data.itemCount
    // @ts-ignore - theme event payload isn't typed
    const itemCount = event.detail?.data?.itemCount;
    
    if (itemCount !== undefined && itemCount === 0) {
      // Cart became empty, clear attributes
      clearCartAttributes();
    }
  }

  /**
   * Check if we're on order confirmation/thank you page
   */
  function isOrderConfirmationPage() {
    // Check for common order confirmation page indicators
    const url = window.location.pathname;
    const orderNumber = new URLSearchParams(window.location.search).get('order');
    
    return (
      url.includes('/thank_you') ||
      url.includes('/order-confirmation') ||
      url.includes('/checkout/thank_you') ||
      orderNumber !== null ||
      document.querySelector('[data-order-confirmation]') !== null ||
      document.querySelector('.order-confirmation') !== null
    );
  }

  /**
   * Clear attributes on order confirmation page
   */
  function handleOrderConfirmation() {
    if (isOrderConfirmationPage()) {
      clearCartAttributes();
    }
  }

  // Initialize: do not clear attributes; keep delivery details and greeting after checkout
  function init() {
    // No cleanup—delivery details and greeting stay in cart attributes even after
    // the customer completes checkout (thank you page) or when cart becomes empty.
  }

  init();
})();
